import { Disciplines } from "~enums/disciplines.ts";
import { FixturesFilterTypes } from "~pages/Fixtures/FixtureListPage/types";
import { FixtureTableColumns } from "~pages/Fixtures/FixtureListPage/types/fixtureColumnsEnum.ts";
import { RosterTableColumns } from "~pages/Roster/enums/rosterTableColumns.ts";
import { FixtureRosterTableColumns } from "~pages/Roster/FixtureRosterListPage/enums/fixtureRosterTableColumns.ts";
import { SeasonRosterTableColumns } from "~pages/Roster/SeasonRosterListPage/enums/seasonRosterTableColumns.ts";
import type { DataTableColumnGroupDefinition } from "~types/dataTableColumnGroupDefinition.ts";
import type { UiConfiguration } from "~types/uiConfiguration.ts";
import { defaultUiConfig } from "./defaultsUi";

const fixtureTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "fixtures.table.column.chooser.title.details",
    columns: [
      { key: FixtureTableColumns.FixtureId },
      { key: FixtureTableColumns.ExternalId },
      { key: FixtureTableColumns.RoundNumber },
      { key: FixtureTableColumns.HomeCompetitor, defaultInColumnChooser: true },
      { key: FixtureTableColumns.Result, defaultInColumnChooser: true },
      { key: FixtureTableColumns.AwayCompetitor, defaultInColumnChooser: true },
      { key: FixtureTableColumns.FixtureNumber, defaultInColumnChooser: true },
      { key: FixtureTableColumns.NameLocal, defaultInColumnChooser: true },
      { key: FixtureTableColumns.NameLatin },
      { key: FixtureTableColumns.VenueId, defaultInColumnChooser: true },
      { key: FixtureTableColumns.StartDate, defaultInColumnChooser: true },
      { key: FixtureTableColumns.StartTime, defaultInColumnChooser: true },
      { key: FixtureTableColumns.FixtureType },
      { key: FixtureTableColumns.Status, defaultInColumnChooser: true },
      { key: FixtureTableColumns.Stage, defaultInColumnChooser: true },
      { key: FixtureTableColumns.StageCode },
      { key: FixtureTableColumns.Pool, defaultInColumnChooser: true },
      { key: FixtureTableColumns.PoolCode },
      { key: FixtureTableColumns.Series },
      { key: FixtureTableColumns.SeriesCode },
      { key: FixtureTableColumns.Added },
      { key: FixtureTableColumns.Attendance },
      { key: FixtureTableColumns.Discipline },
      { key: FixtureTableColumns.StartTimeActualUtc },
      { key: FixtureTableColumns.EndTimeActualUtc },
      { key: FixtureTableColumns.FixtureProfile },
      { key: FixtureTableColumns.ProfileId },
      { key: FixtureTableColumns.InternationalReference },
      { key: FixtureTableColumns.Round },
      { key: FixtureTableColumns.RoundCode },
      { key: FixtureTableColumns.SeriesFixtureNumber },
      { key: FixtureTableColumns.TimesUnconfirmed },
      { key: FixtureTableColumns.StartTimeUtc },
    ],
  },
];

const rosterPersonDetailColumns: DataTableColumnGroupDefinition["columns"] = [
  { key: RosterTableColumns.PersonNameFullLatin },
  { key: RosterTableColumns.PersonAdditionalDetailsHeight },
  { key: RosterTableColumns.PersonAdditionalDetailsWeight },
  { key: RosterTableColumns.PersonAdditionalDetailsHeightImperial },
  { key: RosterTableColumns.PersonAdditionalDetailsWeightImperial },
  { key: RosterTableColumns.PersonAdditionalDetailsRepresentation },
  { key: RosterTableColumns.PersonAdditionalDetailsJuniorAssociationLeague },
  { key: RosterTableColumns.PersonAdditionalDetailsHomeTown },
  { key: RosterTableColumns.PersonAdditionalDetailsCollege },
  { key: RosterTableColumns.PersonAdditionalDetailsCollegeClass },
  { key: RosterTableColumns.PersonAdditionalDetailsSchool },
  { key: RosterTableColumns.PersonAdditionalDetailsSchoolClass },
  { key: RosterTableColumns.Dob },
  { key: RosterTableColumns.PersonGender },
  { key: RosterTableColumns.PersonNationality },
  { key: RosterTableColumns.PersonRepresenting },
  { key: RosterTableColumns.PersonStatus },
];

const seasonRosterTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "roster.table.column.chooser.title.details",
    columns: [
      { key: RosterTableColumns.Bib, defaultInColumnChooser: true },
      { key: RosterTableColumns.NameFullLocal, defaultInColumnChooser: true },
      { key: RosterTableColumns.PersonId, defaultInColumnChooser: true },
      { key: RosterTableColumns.PersonExternalId, defaultInColumnChooser: true },
      { key: RosterTableColumns.ExternalId, defaultInColumnChooser: true },
      { key: RosterTableColumns.Position, defaultInColumnChooser: true },
      { key: RosterTableColumns.IsCaptain, defaultInColumnChooser: true },
      { key: SeasonRosterTableColumns.Status, defaultInColumnChooser: true },
      { key: RosterTableColumns.Caps, defaultInColumnChooser: true },
      ...rosterPersonDetailColumns,
    ],
  },
];

const fixtureRosterTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "roster.table.column.chooser.title.details",
    columns: [
      { key: RosterTableColumns.Bib, defaultInColumnChooser: true },
      { key: RosterTableColumns.NameFullLocal, defaultInColumnChooser: true },
      { key: RosterTableColumns.PersonId, defaultInColumnChooser: true },
      { key: RosterTableColumns.PersonExternalId, defaultInColumnChooser: true },
      { key: RosterTableColumns.ExternalId, defaultInColumnChooser: true },
      { key: RosterTableColumns.Position, defaultInColumnChooser: true },
      { key: FixtureRosterTableColumns.Starter, defaultInColumnChooser: true },
      { key: RosterTableColumns.IsCaptain, defaultInColumnChooser: true },
      { key: RosterTableColumns.Caps, defaultInColumnChooser: true },
      ...rosterPersonDetailColumns,
    ],
  },
];

export const hockeyUiConfig: UiConfiguration = {
  ...defaultUiConfig,
  pages: {
    ...defaultUiConfig.pages,
    fixtures: {
      listPage: {
        filtersConfig: [
          { filterKey: FixturesFilterTypes.Pools },
          { filterKey: FixturesFilterTypes.Competitors },
          { filterKey: FixturesFilterTypes.Venues },
          { filterKey: FixturesFilterTypes.FixtureStatuses },
          { filterKey: FixturesFilterTypes.DateRange },
        ],
        columnsConfig: fixtureTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.fixtures",
          descriptionIntlKey: "import.modal.text.fixtures",
        },
      },
    },
    seasonRoster: {
      listPage: {
        columnsConfig: seasonRosterTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.seasonRoster",
          descriptionIntlKey: "import.modal.text.seasonRoster",
        },
      },
    },
    fixtureRoster: {
      listPage: {
        columnsConfig: fixtureRosterTableColumnsArray,
      },
    },
    seasonUpsert: {
      ...defaultUiConfig.pages.seasonUpsert,
      disciplines: {
        enabled: true,
        defaultDiscipline: Disciplines.Outdoor,
        isNullable: false,
      },
    },
    reports: {
      fixturePlayByPlayFlaggedEventList: { ...defaultUiConfig.pages.reports.fixturePlayByPlayFlaggedEventList },
      reportsConfig: [
        {
          key: "seasonEntities",
          notificationMessageIntlKey: "reports.seasonEntities.started",
          titleIntlKey: "reports.seasonEntities",
          descriptionIntlKey: "reports.seasonEntities.description",
          generateReportUrl: (sport: string, organizationId: string, seasonId: string) =>
            `${sport}/o/${organizationId}/reports/entities/in/season/${seasonId}`,
        },
        {
          key: "seasonCards",
          notificationMessageIntlKey: "reports.seasonCards.started",
          titleIntlKey: "reports.seasonCards",
          descriptionIntlKey: "reports.seasonCards.description",
          generateReportUrl: (sport: string, organizationId: string, seasonId: string) =>
            `${sport}/o/${organizationId}/reports/cards/in/season/${seasonId}`,
        },
        {
          key: "seasonAppointments",
          notificationMessageIntlKey: "reports.seasonAppointments.started",
          titleIntlKey: "reports.seasonAppointments",
          descriptionIntlKey: "reports.seasonAppointments.description",
          generateReportUrl: (sport: string, organizationId: string, seasonId: string) =>
            `${sport}/o/${organizationId}/reports/appointments/in/season/${seasonId}`,
        },
      ],
    },
  },
};
